import type { MetricConfig, PromoMetricGridKpiConfig } from '../../types/config';
import { isDisplayableMetric } from '../metrics';
import type { MetricFormat } from '../metrics';
import type { KpiOption } from '../../PlanDetail/MetricPage/config/kpiCatalog';

/**
 * Backend group key for report KPIs. Symmetric with `PROMO_GROUP_KEYS` /
 * `SPEND_GROUP_KEYS` in `metricAdapter` (which feed the editable grid).
 */
export const KPI_GROUP_KEYS = new Set<string>(['plan_metrics']);

/**
 * Builds the MetricPage KPI catalog directly from `metrics[]` (the entries whose
 * `group` is a KPI group), independent of `metric_views`. Ordered by the
 * metric's `order`. `sortable` defaults to `true` because metric definitions do
 * not carry a per-metric sort flag. Tolerant: returns `[]` when no KPI metrics
 * are present (the report toolbar simply offers no KPIs rather than blocking).
 *
 * `fe_hidden` metrics are excluded: they must never appear in KPI selectors,
 * report columns, or the default-KPI resolution, and must not be sent to the
 * `/report/period-data` / promo-metric endpoints (which reject hidden keys).
 */
export function adaptKpiCatalog(metrics: MetricConfig[]): KpiOption[] {
    return metrics
        .filter((m) => KPI_GROUP_KEYS.has(m.group) && isDisplayableMetric(m))
        .sort((a, b) => a.order - b.order)
        .map((m) => ({ key: m.key, name: m.display_name, sortable: true, fieldType: 'number' as const, meta: { default_visible: m.default_visible } }));
}

/**
 * A requestable KPI's own display format, with every field the config left
 * null/absent OMITTED rather than defaulted. That is what lets `resolveKpiFormat`
 * fall through field by field to the `metrics[]` entry with the same key: a KPI
 * declaring only `currency_prefix` still inherits its precision from `metrics[]`.
 *
 * Returns `undefined` when the config declared none of the three, so the KPI
 * carries no `format` at all.
 */
const toKpiFormat = (kpi: PromoMetricGridKpiConfig): Partial<MetricFormat> | undefined => {
    const format: Partial<MetricFormat> = {};
    if (kpi.precision != null) format.allowedDecimals = kpi.precision;
    if (kpi.currency_prefix != null) format.preUnit = kpi.currency_prefix;
    if (kpi.currency_suffix != null) format.postUnit = kpi.currency_suffix;
    return Object.keys(format).length > 0 ? format : undefined;
};

/**
 * Builds the Promo Metric report KPI catalog from
 * `promo_metric_grid.requestable_kpis[]` — the backend's declaration of which
 * keys the promo-metric endpoints will actually serve. It is a superset of the
 * `plan_metrics` group (it also exposes grid-only inputs and event/mechanic FYI
 * fields), so it cannot be derived from `metrics[]`.
 *
 * `planKpiCatalog` is returned unchanged when the field is absent or empty, which
 * keeps a not-yet-migrated backend on today's behaviour instead of emptying the
 * Promo Metric KPI selector.
 */
export function adaptPromoKpiCatalog(
    requestableKpis: PromoMetricGridKpiConfig[] | undefined,
    planKpiCatalog: KpiOption[],
): KpiOption[] {
    if (!requestableKpis?.length) {
        // Loud, because the two catalogs look similar enough in the picker that a
        // silent fallback reads as "my change did not take effect".
        console.warn(
            '[promoKpiCatalog] config has no promo_metric_grid.requestable_kpis — the Promo Metric KPI picker is ' +
                'falling back to the metrics[] plan_metrics catalog.',
        );
        return planKpiCatalog;
    }

    return [...requestableKpis]
        .sort((a, b) => a.order - b.order)
        .map((k) => {
            const format = toKpiFormat(k);
            return {
                key: k.key,
                name: k.display_name,
                sortable: true,
                fieldType: k.field_type,
                ...(format ? { format } : {}),
                meta: { default_visible: k.default_visible },
            };
        });
}
